/**
 * useCartCheckout Hook
 *
 * Creates all Xaman payloads upfront when checkout starts (pushed to wallet),
 * then polls their statuses until all are resolved. No sequential signing needed.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useAuthStore } from '../store/authStore';
import { useCartStore, type CartItem } from '../store/cartStore';
import { apiClient } from '../api/client';
import type { SigningPayload } from '../types/signing';
import { toSigningPayload, getWalletProvider } from '../lib/wallet';
import { isSupportedMarketplace, calculateBuyAmount } from '../lib/marketplace-constants';
import { calculateCartTotals, type CartTotals } from '../utils/cartTotals';

export type CartCheckoutStage = 'idle' | 'confirming' | 'purchasing' | 'complete';

export type CheckoutItemStatus = 'creating' | 'pending' | 'signed' | 'rejected' | 'expired' | 'error';

export interface CheckoutItemState {
  item: CartItem;
  payload: SigningPayload | null;
  status: CheckoutItemStatus;
  error?: string;
}

export interface CartPurchaseFailure {
  tokenId: string;
  name: string;
  error: string;
}

export interface CartCheckoutState {
  stage: CartCheckoutStage;
  checkoutItems: CheckoutItemState[];
  completedPurchases: string[];
  failedPurchases: CartPurchaseFailure[];
  // Client-signing wallets (joey-wc) can't push N payloads or sign in parallel over one
  // WC session — they sign SEQUENTIALLY via a per-item SigningStepMonitor in the modal.
  // Xaman keeps the parallel push + polling model.
  isClientSigning: boolean;
}

const initialState: CartCheckoutState = {
  stage: 'idle',
  checkoutItems: [],
  completedPurchases: [],
  failedPurchases: [],
  isClientSigning: false,
};

function deriveResults(items: CheckoutItemState[]) {
  return {
    completedPurchases: items
      .filter((i) => i.status === 'signed')
      .map((i) => i.item.nft_token_id),
    failedPurchases: items
      .filter((i) => i.status === 'rejected' || i.status === 'expired' || i.status === 'error')
      .map((i) => ({
        tokenId: i.item.nft_token_id,
        name: i.item.name || `NFT #${i.item.nft_token_id.slice(-8)}`,
        error:
          i.error ||
          (i.status === 'expired'
            ? 'Transaction expired'
            : i.status === 'rejected'
              ? 'Transaction rejected'
              : 'Unknown error'),
      })),
  };
}

function isResolved(status: CheckoutItemStatus): boolean {
  return status === 'signed' || status === 'rejected' || status === 'expired' || status === 'error';
}

export function useCartCheckout() {
  const { user } = useAuthStore();
  const [state, setState] = useState<CartCheckoutState>(initialState);
  const [itemsToCheckout, setItemsToCheckout] = useState<CartItem[]>([]);
  const checkoutItemsRef = useRef<CheckoutItemState[]>([]);
  const cancelledRef = useRef(false);

  const totals: CartTotals = calculateCartTotals(itemsToCheckout);

  // Keep ref in sync
  useEffect(() => {
    checkoutItemsRef.current = state.checkoutItems;
  }, [state.checkoutItems]);

  // Poll payload statuses while purchasing (Xaman only — client-signing resolves
  // via per-item SigningStepMonitor in the modal, no /offers/payload polling).
  useEffect(() => {
    if (state.stage !== 'purchasing') return;
    if (state.isClientSigning) return;

    const poll = async () => {
      const items = checkoutItemsRef.current;
      const pendingItems = items.filter(
        (i) => i.payload && (i.status === 'pending' || i.status === 'creating')
      );

      // If nothing left to poll, check if all resolved
      if (pendingItems.length === 0) {
        const allDone = items.length > 0 && items.every((i) => isResolved(i.status));
        if (allDone) {
          setState((s) => {
            const results = deriveResults(s.checkoutItems);
            return { ...s, stage: 'complete', ...results };
          });
        }
        return;
      }

      for (const ci of pendingItems) {
        if (!ci.payload) continue;
        try {
          const response = await apiClient.get<any>(`/offers/payload/${ci.payload.id}`);
          if (!response.success || !response.data) continue;

          const { signed, resolved, expired } = response.data;
          let newStatus: CheckoutItemStatus | null = null;

          if (signed === true) {
            newStatus = 'signed';
          } else if (expired) {
            newStatus = 'expired';
          } else if (resolved && signed === false) {
            newStatus = 'rejected';
          }

          if (newStatus) {
            const tokenId = ci.item.nft_token_id;
            setState((s) => {
              const updated = s.checkoutItems.map((item) =>
                item.item.nft_token_id === tokenId
                  ? { ...item, status: newStatus! }
                  : item
              );
              const allDone = updated.every((i) => isResolved(i.status));
              const results = deriveResults(updated);
              return {
                ...s,
                checkoutItems: updated,
                ...results,
                ...(allDone ? { stage: 'complete' as const } : {}),
              };
            });
          }
        } catch {
          // Polling error — retry next cycle
        }
      }
    };

    const interval = setInterval(poll, 3000);
    // Also poll immediately
    poll();

    return () => clearInterval(interval);
  }, [state.stage, state.isClientSigning]);

  /**
   * Start the confirmation stage with current cart items
   */
  const startConfirmation = useCallback((items: CartItem[]) => {
    setItemsToCheckout(items);
    setState({ ...initialState, stage: 'confirming' });
  }, []);

  /**
   * Create Xaman payload for a cart item purchase
   */
  const createPurchasePayload = useCallback(
    async (item: CartItem): Promise<SigningPayload> => {
      if (!user?.xrpl_address) throw new Error('Not authenticated');

      const isBrokered = !!item.destination && isSupportedMarketplace(item.destination);

      let response;
      if (isBrokered) {
        // Marketplace-brokered: create matching buy offer (same as NFTDetailPage Buy Now)
        const buyOfferAmount = calculateBuyAmount(item.price_drops, item.destination!);
        response = await apiClient.post<any>('/offers/create', {
          nftTokenId: item.nft_token_id,
          offerType: 'buy',
          amount: buyOfferAmount,
          owner: item.owner_address,
          userAddress: user.xrpl_address,
          destination: item.destination,
        });
      } else {
        // P2P: directly accept the sell offer
        response = await apiClient.post<any>('/offers/accept-p2p', {
          nftTokenId: item.nft_token_id,
          offerIndex: item.offer_id,
          userAddress: user.xrpl_address,
          offerType: 'sell',
        });
      }

      if (response.success && (response.data?.signingIntent || response.data?.payload)) {
        return toSigningPayload(response.data.signingIntent ?? response.data.payload);
      }
      throw new Error(response.error || 'Failed to create payload');
    },
    [user?.xrpl_address]
  );

  /**
   * Start purchasing — create ALL payloads upfront
   */
  const startPurchasing = useCallback(async () => {
    if (itemsToCheckout.length === 0) return;
    cancelledRef.current = false;

    // Initialize all items as 'creating'
    const initialItems: CheckoutItemState[] = itemsToCheckout.map((item) => ({
      item,
      payload: null,
      status: 'creating' as const,
    }));

    setState({
      stage: 'purchasing',
      checkoutItems: initialItems,
      completedPurchases: [],
      failedPurchases: [],
      isClientSigning: false,
    });

    // Create all payloads sequentially (to avoid rate limiting). For both providers
    // we create the server-side buy offers upfront; resolution differs by provider
    // (Xaman polls in parallel, client-signing signs each item sequentially).
    for (let i = 0; i < itemsToCheckout.length; i++) {
      if (cancelledRef.current) break;
      const item = itemsToCheckout[i];
      try {
        const payload = await createPurchasePayload(item);
        const clientSigning = getWalletProvider(payload.provider).capabilities.clientSigning;
        setState((s) => ({
          ...s,
          isClientSigning: s.isClientSigning || clientSigning,
          checkoutItems: s.checkoutItems.map((ci, idx) =>
            idx === i ? { ...ci, payload, status: 'pending' as const } : ci
          ),
        }));
      } catch (err: any) {
        setState((s) => ({
          ...s,
          checkoutItems: s.checkoutItems.map((ci, idx) =>
            idx === i ? { ...ci, status: 'error' as const, error: err.message } : ci
          ),
        }));
      }
    }

    // After all payloads created, check if everything already failed
    setState((s) => {
      const allDone = s.checkoutItems.every((i) => isResolved(i.status));
      if (allDone) {
        const results = deriveResults(s.checkoutItems);
        return { ...s, stage: 'complete', ...results };
      }
      return s;
    });
  }, [itemsToCheckout, createPurchasePayload]);

  /**
   * Resolve a single client-signing (joey-wc) item after its sequential
   * SigningStepMonitor settles. Updates that item's status and, when all items
   * are resolved, advances to the complete stage.
   */
  const resolveClientItem = useCallback(
    (tokenId: string, status: Extract<CheckoutItemStatus, 'signed' | 'rejected' | 'expired' | 'error'>, error?: string) => {
      setState((s) => {
        const updated = s.checkoutItems.map((ci) =>
          ci.item.nft_token_id === tokenId && !isResolved(ci.status)
            ? { ...ci, status, ...(error ? { error } : {}) }
            : ci
        );
        const allDone = updated.length > 0 && updated.every((i) => isResolved(i.status));
        const results = deriveResults(updated);
        return {
          ...s,
          checkoutItems: updated,
          ...results,
          ...(allDone ? { stage: 'complete' as const } : {}),
        };
      });
    },
    []
  );

  /**
   * Cancel remaining purchases
   */
  const cancelRemaining = useCallback(() => {
    cancelledRef.current = true;
    setState((s) => {
      const updated = s.checkoutItems.map((i) =>
        !isResolved(i.status)
          ? { ...i, status: 'rejected' as const, error: 'Cancelled' }
          : i
      );
      const results = deriveResults(updated);
      return { ...s, checkoutItems: updated, stage: 'complete', ...results };
    });
  }, []);

  /**
   * Complete checkout: remove purchased items from cart
   */
  const completeCheckout = useCallback(() => {
    const completed = state.completedPurchases;
    if (completed.length > 0) {
      useCartStore.getState().removeItems(completed);
    }
    setState(initialState);
    setItemsToCheckout([]);
  }, [state.completedPurchases]);

  /**
   * Reset / cancel confirmation
   */
  const reset = useCallback(() => {
    cancelledRef.current = true;
    setState(initialState);
    setItemsToCheckout([]);
  }, []);

  return {
    state,
    itemsToCheckout,
    totals,

    startConfirmation,
    startPurchasing,
    cancelRemaining,
    resolveClientItem,
    completeCheckout,
    reset,
  };
}
